import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  BookOpen, Dog, ChevronRight, Search, Loader2, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function Blog() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles`);
        setArticles(res.data.articles || []);
      } catch (e) {
        console.error('Error fetching articles:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const filteredArticles = articles.filter(a => 
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.breed?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Helmet>
        <title>Blog o Rasach Psów i Kotów | ApisAI</title>
        <meta name="description" content="Przewodniki po rasach psów i kotów. Dowiedz się wszystkiego o charakterze, zdrowiu i pielęgnacji. Znajdź idealną rasę dla siebie." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://apisai.pl/blog" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0B0F1A] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Home className="w-5 h-5 text-gray-400" />
              <span className="text-amber-400 font-bold">ApisAI</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/blog" className="text-white font-medium">Blog</Link>
              <Link to="/login" className="text-gray-400 hover:text-white">Zaloguj</Link>
              <Link to="/rejestracja">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-full">
                  Dodaj hodowlę
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-[#0B0F1A] to-[#10182B]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm mb-6">
              <BookOpen className="w-4 h-4" />
              Przewodniki po rasach
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Blog o Rasach Psów
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Wszystko co musisz wiedzieć przed wyborem szczeniaka. Charakter, zdrowie, pielęgnacja i ceny.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Szukaj rasy..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">Nie znaleziono artykułów</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, i) => (
                <motion.div
                  key={article.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link 
                    to={`/blog/${article.slug}`}
                    className="block h-full bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/5 group"
                  >
                    {/* Image placeholder */}
                    <div className="aspect-video bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                      <Dog className="w-16 h-16 text-amber-400/50" />
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-amber-400 text-sm mb-2">
                        <BookOpen className="w-4 h-4" />
                        <span>Przewodnik</span>
                      </div>
                      <h2 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {article.meta_description}
                      </p>
                      <div className="flex items-center text-amber-400 text-sm font-medium">
                        Czytaj więcej
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-500/5 to-orange-500/5 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Jesteś hodowcą?
          </h2>
          <p className="text-gray-400 mb-8">
            Dołącz do ApisAI i pokaż swoją hodowlę tysiącom klientów szukających szczeniąt przez AI.
          </p>
          <Link to="/rejestracja">
            <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full px-8 py-6 text-lg">
              Dodaj hodowlę za darmo
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} ApisAI — Platforma dla hodowców widoczna w AI
          </p>
        </div>
      </footer>
    </div>
  );
}
